import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Container,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Chip
} from '@mui/material';

import { addToWaitingList, getWaitingList } from '../firebase/services/waitingListService';
import { createCustomer, getCustomerByEmail } from '../firebase/services/customerService';

const vesselTypes = [
  'Sailboat',
  'Motor Yacht',
  'Powerboat',
  'Catamaran',
  'Houseboat',
  'Other'
];

const slipTypes = [
  'Standard',
  'Premium',
  'Oversized'
];

const states = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const formatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');
  
  // Format as (xxx) xxx-xxxx
  if (numbers.length >= 10) {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  } else if (numbers.length >= 6) {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6)}`;
  } else if (numbers.length >= 3) {
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  }
  return numbers;
};

const unformatPhoneNumber = (value: string): string => {
  // Remove all non-numeric characters
  return value.replace(/\D/g, '');
};

const WaitingListForm: React.FC = () => {
  const navigate = useNavigate();
  const [waitingListCount, setWaitingListCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    customerEmail: '',
    customerPhone: '',
    cellPhone: '',
    isHomeowner: undefined,
    mailingAddress: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    localAddress: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    vesselName: '',
    vesselYear: '',
    vesselMake: '',
    vesselModel: '',
    vesselColor: '',
    vesselLength: 0,
    vesselRegistrationNumber: '',
    vesselRegistrationState: '',
    vesselRegistrationYear: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  useEffect(() => {
    const fetchWaitingListCount = async () => {
      try {
        const entries = await getWaitingList();
        setWaitingListCount(entries.length);
      } catch (err) {
        console.error('Error fetching waiting list count:', err);
      } finally {
        setCountLoading(false);
      }
    };

    fetchWaitingListCount();
  }, []);

  useEffect(() => {
    if (error) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Handle phone number formatting
    if (name === 'customerPhone' || name === 'cellPhone') {
      const formattedValue = formatPhoneNumber(value);
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent as keyof typeof prev],
            [child]: formattedValue
          }
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: formattedValue
        }));
      }
      return;
    }
    
    // Handle nested address fields
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent as keyof typeof prev],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Validate form data
      if (!formData.firstName || !formData.lastName || !formData.customerEmail || !formData.cellPhone) {
        throw new Error('Please fill in all required fields');
      }

      // Validate phone numbers
      const cellPhoneDigits = unformatPhoneNumber(formData.cellPhone);
      if (cellPhoneDigits.length !== 10) {
        throw new Error('Cell phone number must be 10 digits');
      }

      if (formData.customerPhone) {
        const homePhoneDigits = unformatPhoneNumber(formData.customerPhone);
        if (homePhoneDigits.length !== 10) {
          throw new Error('Home phone number must be 10 digits');
        }
      }

      // Check if customer already exists on waiting list
      const customerRecord = await getCustomerByEmail(formData.customerEmail);
      if (customerRecord) {
        const waitingList = await getWaitingList();
        const isOnWaitingList = waitingList.some(entry => 
          entry.customerEmail.toLowerCase() === formData.customerEmail.toLowerCase() && 
          entry.status === 'pending'
        );
        
        if (isOnWaitingList) {
          throw new Error('You are already on the waiting list. We will contact you when a slip becomes available.');
        }
      }

      // Validate local address if homeowner
      if (formData.isHomeowner === true) {
        if (!formData.localAddress.street || !formData.localAddress.city || 
            !formData.localAddress.state || !formData.localAddress.zip) {
          throw new Error('Please provide your local address as a homeowner');
        }
      }

      if (!formData.vesselLength || !formData.vesselYear || 
          !formData.vesselMake || !formData.vesselModel || !formData.vesselColor) {
        throw new Error('Please provide all required vessel details');
      }

      // Check if customer exists
      let customerId: string;
      if (customerRecord) {
        customerId = customerRecord.id!;
      } else {
        // Create new customer with unformatted phone numbers
        const newCustomer = await createCustomer({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.customerEmail,
          phone: unformatPhoneNumber(formData.customerPhone),
          status: 'active',
          dateCreated: new Date()
        });
        customerId = newCustomer.id!;
      }

      // Convert numeric fields and add to waiting list
      const entry = {
        ...formData,
        customerId,
        vesselLength: parseFloat(formData.vesselLength.toString()),
        status: 'pending' as const,
        priority: 0,
        dateAdded: new Date()
      };

      await addToWaitingList(entry);
      setSuccess(true);
      setShowSuccessDialog(true);
      
      // Reset form data
      setFormData({
        firstName: '',
        lastName: '',
        customerEmail: '',
        customerPhone: '',
        cellPhone: '',
        isHomeowner: undefined,
        mailingAddress: {
          street: '',
          city: '',
          state: '',
          zip: ''
        },
        localAddress: {
          street: '',
          city: '',
          state: '',
          zip: ''
        },
        vesselName: '',
        vesselYear: '',
        vesselMake: '',
        vesselModel: '',
        vesselColor: '',
        vesselLength: 0,
        vesselRegistrationNumber: '',
        vesselRegistrationState: '',
        vesselRegistrationYear: '',
        notes: ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit form');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    navigate('/'); // Redirect to home page
  };

  return (
    <Container maxWidth="md">
      <Box py={4}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center">
            Marina Slip Waiting List
          </Typography>
          
          <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
            <Chip
              label={countLoading ? (
                <CircularProgress size={20} />
              ) : (
                `${waitingListCount} ${waitingListCount === 1 ? 'person' : 'people'} currently on the waiting list`
              )}
              color="primary"
              variant="outlined"
              sx={{ fontSize: '1rem', py: 1 }}
            />
          </Box>
          
          <Typography variant="body1" paragraph align="center" color="text.secondary">
            Fill out the form below to join our waiting list for marina slips. We'll contact you when a slip becomes available.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Thank you for joining our waiting list! We'll contact you when a slip becomes available.
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                  Contact Information
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="customerEmail"
                      type="email"
                      value={formData.customerEmail}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Home Phone"
                      name="customerPhone"
                      value={formData.customerPhone}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cell Phone"
                      name="cellPhone"
                      value={formData.cellPhone}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                  Mailing Address
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      name="mailingAddress.street"
                      value={formData.mailingAddress.street}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      name="mailingAddress.city"
                      value={formData.mailingAddress.city}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="State"
                      name="mailingAddress.state"
                      value={formData.mailingAddress.state}
                      onChange={handleChange}
                      required
                      select
                      InputLabelProps={{ shrink: true }}
                    >
                      {states.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      name="mailingAddress.zip"
                      value={formData.mailingAddress.zip}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                  Local Address {formData.isHomeowner === true && '(Required for homeowners)'}
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FormLabel component="legend" sx={{ textAlign: 'center', width: '100%' }}>
                        Are you a homeowner in the Fallen Leaf basin? *
                      </FormLabel>
                      <RadioGroup
                        row
                        value={formData.isHomeowner === undefined ? '' : formData.isHomeowner ? 'true' : 'false'}
                        onChange={(e) => setFormData({ ...formData, isHomeowner: e.target.value === 'true' })}
                        sx={{ justifyContent: 'center' }}
                      >
                        <FormControlLabel value="true" control={<Radio />} label="Yes" />
                        <FormControlLabel value="false" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Street Address"
                      name="localAddress.street"
                      value={formData.localAddress.street}
                      onChange={handleChange}
                      required={formData.isHomeowner === true}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="City"
                      name="localAddress.city"
                      value={formData.localAddress.city}
                      onChange={handleChange}
                      required={formData.isHomeowner === true}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="State"
                      name="localAddress.state"
                      value={formData.localAddress.state}
                      onChange={handleChange}
                      required={formData.isHomeowner === true}
                      select
                      InputLabelProps={{ shrink: true }}
                    >
                      {states.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      name="localAddress.zip"
                      value={formData.localAddress.zip}
                      onChange={handleChange}
                      required={formData.isHomeowner === true}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                  Vessel Information
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Vessel Name (Optional)"
                      name="vesselName"
                      value={formData.vesselName}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vessel Year"
                      name="vesselYear"
                      value={formData.vesselYear}
                      onChange={handleChange}
                      required
                      inputProps={{ 
                        maxLength: 4,
                        pattern: '[0-9]*'
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vessel Make"
                      name="vesselMake"
                      value={formData.vesselMake}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vessel Model"
                      name="vesselModel"
                      value={formData.vesselModel}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vessel Color"
                      name="vesselColor"
                      value={formData.vesselColor}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vessel Length (ft)"
                      name="vesselLength"
                      type="number"
                      value={formData.vesselLength}
                      onChange={handleChange}
                      inputProps={{ 
                        min: 0, 
                        max: 99,
                        step: 0.1
                      }}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vessel Registration #"
                      name="vesselRegistrationNumber"
                      value={formData.vesselRegistrationNumber}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vessel Registration State"
                      name="vesselRegistrationState"
                      value={formData.vesselRegistrationState}
                      onChange={handleChange}
                      required
                      select
                      InputLabelProps={{ shrink: true }}
                    >
                      {states.map((state) => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vessel Registration Year"
                      name="vesselRegistrationYear"
                      value={formData.vesselRegistrationYear}
                      onChange={handleChange}
                      required
                      inputProps={{ 
                        maxLength: 4,
                        pattern: '[0-9]*'
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                  Additional Information
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional Notes"
                      name="notes"
                      multiline
                      rows={4}
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Please provide any additional information that might help us find the right slip for you."
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Join Waiting List'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
      <Dialog
        open={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Success!</DialogTitle>
        <DialogContent>
          <Typography>
            Thank you for joining our waiting list! We'll contact you when a slip becomes available.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogClose} variant="contained" color="primary">
            Return to Home
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WaitingListForm; 